// Cairo ships inside the app (no Google Fonts dependency on phones / offline PWA); Inter stays remote for the English UI.
import '@fontsource-variable/cairo'
import '@fontsource-variable/inter'
import { QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from 'sonner'
import { queryClient } from '@/app/queryClient'
import { router } from '@/app/router'
import { notify, TooltipProvider } from '@/design-system'
import { useAuth } from '@/stores/auth'
import { applyUiEffects, useUi } from '@/stores/ui'
import '@/design-system/tokens.css'
import '@/lib/pwa'
import { applyOpsManifest, healOpsShell, isOpsPath } from '@/features/operations/opsApp'
import { applyAccountsManifest, isAccountsPath } from '@/features/accounts/accountsApp'
import { checkForUpdate, markUpdateAvailable } from '@/lib/appUpdate'

// the ops app installs as its own app (features/operations/opsApp.ts)
if (isOpsPath(location.pathname)) {
  void healOpsShell()
  applyOpsManifest()
}
// so does تطبيق الحسابات (features/accounts/accountsApp.ts)
if (isAccountsPath(location.pathname)) applyAccountsManifest()
applyUiEffects()
void useAuth.getState().bootstrap()

// Last line of defence: a thrown error or rejected promise that no try/catch or React Query cache reached
// still surfaces as a loud, visible error instead of failing silently while Operations watches the screen.
/**
 * Browser noise that is not the office's problem and must not pop a red box: the service worker failing to update
 * (its script is momentarily missing while a new build is copied onto the server, and it retries by itself), and
 * the harmless ResizeObserver loop warning. They go to the console instead.
 */
const NOISE = /ServiceWorker|service worker|ResizeObserver loop|The object is in an invalid state/i
const isNoise = (v: unknown) => NOISE.test(String((v as { message?: string })?.message ?? v ?? ''))
window.addEventListener('error', (e) => (isNoise(e.error ?? e.message) ? console.warn('[ignored]', e.error ?? e.message) : notify.error(e.error ?? e.message)))
window.addEventListener('unhandledrejection', (e) => (isNoise(e.reason) ? console.warn('[ignored]', e.reason) : notify.error(e.reason)))
// A new build renames the page chunks; a tab still running the old shell would fail to open any page it
// hasn't visited yet. Reload once instead of showing an error (Vite fires this on a failed dynamic import).
// A new build (the service worker taking over, or a newer /version.json on the server) shows the update prompt
// (layout/SystemBanners UpdatePrompt); the page reloads only when the user taps it.
if ('serviceWorker' in navigator) {
  // only a *replacement* controller means "new build" - the first install on a fresh browser is not an update
  const hadController = Boolean(navigator.serviceWorker.controller)
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) markUpdateAvailable('sw')
  })
}
if (import.meta.env.PROD) {
  void checkForUpdate(__BUILD__)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void checkForUpdate(__BUILD__) })
  setInterval(() => void checkForUpdate(__BUILD__), 2 * 60_000)
}
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault()
  const key = 'aos:reloaded-for-chunk'
  if (sessionStorage.getItem(key) === location.href) return
  sessionStorage.setItem(key, location.href)
  location.reload()
})

function ThemedToaster() {
  const locale = useUi((s) => s.locale)
  return <Toaster position={locale === 'ar' ? 'bottom-left' : 'bottom-right'} dir={locale === 'ar' ? 'rtl' : 'ltr'} richColors closeButton toastOptions={{ className: 'font-sans' }} />
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <RouterProvider router={router} />
        <ThemedToaster />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
)
